import { FCX } from "@datassential/platform-ui-lib";
import SelectUserBrand from "@vms/SelectAmong/SelectUserBrand";
import { SelectAmongData } from "@vms/SelectAmong/SelectAmongContext";
import {
    useBrands,
    useCStore, useDeliveryServices,
    useHotels,
    useOnSites,
    useRestaurants,
    useSegments,
    useSupermarkets
} from "@core/CommonDataContext";
import { useIsUserBrandsSelected } from "@vms/SelectAmong/useIsUserBrandsSelected";
import { useMemo } from "react";

const SelectAmongUserBrands: FCX<{
    data: SelectAmongData;
    dispatchPartial: (partialData: Partial<SelectAmongData>) => void;
    useCoExclusions: boolean;
    allowMultiSelect?: boolean;
}> = ({
    data,
    dispatchPartial,
    useCoExclusions,
    allowMultiSelect,
}) => {
    const restaurantsOptions = useRestaurants();
    const cStoreOptions= useCStore();
    const supermarketsOptions = useSupermarkets();
    const hotelsOptions = useHotels();
    const brandsOptions = useBrands();
    const segmentsOptions = useSegments();
    const onSiteOptions = useOnSites();
    const deliveryOptions = useDeliveryServices();

    const isAnySelected = useIsUserBrandsSelected(data);

    const isDisabled = useCoExclusions && isAnySelected;
    const defaultProps = useMemo(
        () => ({
            isMulti: allowMultiSelect,
        }),
        [allowMultiSelect]
    );

    return (
        <>
            <SelectUserBrand
                value={data.restaurants}
                setValue={(value) => dispatchPartial({ restaurants: value })}
                options={restaurantsOptions.map((o) => ({
                    id: o.id ?? 0,
                    title: o.name ?? '',
                }))}
                placeholder="Restaurants"
                disabled={isDisabled && data.restaurants.length === 0}
                {...defaultProps}
            />
            <SelectUserBrand
                value={data.cStores}
                setValue={(value) => dispatchPartial({ cStores: value })}
                options={cStoreOptions.map((o) => ({
                    id: o.id ?? 0,
                    title: o.name ?? '',
                }))}
                placeholder="C-Stores"
                disabled={isDisabled && data.cStores.length === 0}
                {...defaultProps}
            />
            <SelectUserBrand
                value={data.supermarkets}
                setValue={(value) => dispatchPartial({ supermarkets: value })}
                options={supermarketsOptions.map((o) => ({
                    id: o.id ?? 0,
                    title: o.name ?? '',
                }))}
                placeholder="Supermarkets"
                disabled={isDisabled && data.supermarkets.length === 0}
                {...defaultProps}
            />
            <SelectUserBrand
                value={data.hotels}
                setValue={(value) => dispatchPartial({ hotels: value })}
                options={hotelsOptions.map((o) => ({
                    id: o.id ?? 0,
                    title: o.name ?? '',
                }))}
                placeholder="Hotels"
                disabled={isDisabled && data.hotels.length === 0}
                {...defaultProps}
            />
            <SelectUserBrand
                value={data.brands}
                setValue={(value) => dispatchPartial({ brands: value })}
                options={brandsOptions.map((o) => ({
                    id: o.id ?? 0,
                    title: o.name ?? '',
                }))}
                placeholder="CPG Brands"
                disabled={isDisabled && data.brands.length === 0}
                {...defaultProps}
            />
            <SelectUserBrand
                value={data.segmentsAndOnSites}
                setValue={(value) => dispatchPartial({ segmentsAndOnSites: value })}
                options={[...segmentsOptions, ...onSiteOptions].map((o) => ({
                    id: o.id ?? 0,
                    title: o.name ?? '',
                }))}
                keepOrder
                placeholder="Segments"
                disabled={isDisabled && data.segmentsAndOnSites.length === 0}
                {...defaultProps}
            />
            <SelectUserBrand
                value={data.deliveryServices}
                setValue={(value) => dispatchPartial({ deliveryServices: value })}
                options={deliveryOptions.map((o) => ({
                    id: o.id ?? 0,
                    title: o.name ?? '',
                }))}
                placeholder="Delivery Services"
                disabled={isDisabled && data.deliveryServices.length === 0}
                {...defaultProps}
            />
        </>
    );
};

export default SelectAmongUserBrands;